import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Droplets, Filter, Beaker, Sparkles, Zap, Leaf, ChevronDown } from "lucide-react";
import PageHero from "../components/PageHero";
import { useMedia } from "../hooks/useMedia";

const STAGES = [
  { n: "01", label: "Tap Water", tds: "~300 ppm", desc: "Municipal supply. Calcium, chlorine, silica, and dissolved iron.", Icon: Droplets },
  { n: "02", label: "Sediment + Carbon Filter", tds: "~280 ppm", desc: "Traps particulates ≥ 5 microns. Adsorbs chlorine and organics.", Icon: Filter },
  { n: "03", label: "Reverse Osmosis Membrane", tds: "~15 ppm", desc: "Semi-permeable membrane rejects 95%+ of dissolved solids.", Icon: Beaker },
  { n: "04", label: "Deionising Resin Bed", tds: "0 ppm", desc: "Anion + cation resins swap remaining ions for pure H⁺ and OH⁻.", Icon: Sparkles },
  { n: "05", label: "Spot-Free Rinse", tds: "0 ppm", desc: "Delivered through a water-fed pole. Dries perfectly clear.", Icon: Zap },
];

const COMPARISONS = [
  { dim: "Cleaning agent", trad: "Soap or ammonia-based glass cleaner", di: "Pure H₂O — no surfactants, no residue" },
  { dim: "Result on drying", trad: "Streaks, mineral spots, film build-up", di: "Streak-free, spot-free, film-free" },
  { dim: "Frequency needed", trad: "Every 4–6 weeks (chemical residue attracts new dirt)", di: "Every 8–12 weeks — residue-free surface stays cleaner longer" },
  { dim: "Height reach", trad: "Ladders + rope access up to 3 stories", di: "Water-fed pole reaches 5 stories from the ground — no ladders" },
  { dim: "Safety", trad: "Elevated fall risk, ladder incidents", di: "Ground-based, zero fall exposure" },
  { dim: "Environment", trad: "Chemical runoff into storm drains", di: "Zero surfactant runoff, pure water only" },
];

const FAQ = [
  {
    q: "How is deionised water different from distilled water?",
    a: "Distilled water is boiled and condensed; deionised water is passed through anion + cation resin beds that swap dissolved minerals for pure H⁺ and OH⁻ ions. Both reach ~0 total dissolved solids, but DI is far more efficient at commercial scale and produces the exact 'aggressive' water quality needed to lift dirt from glass without leaving residue.",
  },
  {
    q: "Why doesn't the crew use soap?",
    a: "Soap surfactants leave a microscopic residue on glass that (a) shows as streaks in low-angle sunlight and (b) attracts new dirt within days, shortening the interval between cleanings. Pure DI water at 0 TDS has a natural affinity for dissolved solids, lifting them off the glass on contact.",
  },
  {
    q: "Is a water-fed pole really safer than ladders?",
    a: "Ladders are the leading cause of injury in the exterior maintenance industry. Our water-fed poles (carbon fibre, telescoping to fifty feet) allow us to clean up to five stories from the sidewalk with zero fall exposure. Your insurance carrier will love it.",
  },
  {
    q: "Are DI systems more expensive than traditional cleaning?",
    a: "The rig is more capital-intensive up front (RO membrane + resin beds run five figures), but the operational cost per pane is comparable — and because DI-cleaned glass stays clean roughly 40–50% longer, the total annual bill for your property typically decreases.",
  },
  {
    q: "Does DI water damage historic glass or window film?",
    a: "No. DI water is chemically the softest water you can produce. It cleans without any additives that could interact with tinting, security film, low-E coatings, or leaded glass. It is the preferred cleaning method for museums and landmark restoration projects.",
  },
];

function StageCard({ s, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, delay: i * 0.1 }}
      className="glass-card p-8 relative"
      data-testid={`stage-${s.n}`}
    >
      <div className="flex items-center justify-between">
        <div className="w-14 h-14 flex items-center justify-center" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", background: "rgba(198,168,124,0.08)" }}>
          <s.Icon size={20} strokeWidth={1.4} style={{ color: "var(--gold)" }} />
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--text-3)" }}>{s.n}</div>
      </div>
      <h3 className="mt-6 font-display text-xl md:text-2xl" style={{ letterSpacing: "-0.02em" }}>{s.label}</h3>
      <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--gold-2)" }}>{s.tds}</div>
      <p className="mt-5 font-body" style={{ color: "var(--text-2)", lineHeight: 1.65, fontSize: 15 }}>{s.desc}</p>
    </motion.div>
  );
}

function FaqRow({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: i * 0.05 }}
      style={{ borderTop: "1px solid var(--line)" }}
      data-testid={`faq-${i}`}
    >
      <button onClick={() => setOpen(!open)} className="w-full py-8 flex items-center justify-between text-left group" data-cursor="hover">
        <span className="font-display text-xl md:text-2xl group-hover:text-[var(--gold-2)] transition-colors" style={{ letterSpacing: "-0.015em" }}>{q}</span>
        <ChevronDown size={18} className="transition-transform duration-500 shrink-0 ml-6" style={{ color: "var(--gold)", transform: open ? "rotate(180deg)" : "rotate(0)" }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="pb-8 font-body max-w-3xl overflow-hidden"
            style={{ color: "var(--text-2)", lineHeight: 1.8, fontSize: 16 }}
          >
            {a}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function DiWater() {
  const { url } = useMedia();

  return (
    <div className="min-h-screen" data-testid="di-water-page">
      <PageHero
        eyebrow="DI Water Technology"
        title={<>Zero <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>total dissolved solids.</span></>}
        subtitle="The reason our windows stay clean longer than the vendor who was there before us. A short study on why we do it and what it means for your property."
        image={url("di-water-hero") || url("window-cleaning")}
        kicker="Water chemistry, plainly explained."
      />

      {/* The Diagram */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">The Filtration Chain</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              300 parts per million <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>to zero.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {STAGES.map((s, i) => <StageCard key={s.n} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg-2)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">Traditional vs DI</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              The <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>measurable</span> difference.
            </h2>
          </div>

          <div className="rounded-[24px] overflow-hidden" style={{ border: "1px solid var(--line)", background: "rgba(11,11,13,0.6)" }}>
            <div className="grid md:grid-cols-3 font-mono text-[10px] uppercase tracking-[0.36em] py-5 px-6 md:px-10" style={{ background: "rgba(198,168,124,0.06)", color: "var(--text-3)", borderBottom: "1px solid var(--line)" }}>
              <div>Dimension</div>
              <div>Traditional soap-and-squeegee</div>
              <div style={{ color: "var(--gold)" }}>Bespoke DI System</div>
            </div>
            {COMPARISONS.map((c, i) => (
              <div key={c.dim} className="grid md:grid-cols-3 gap-4 md:gap-8 py-8 px-6 md:px-10" style={{ borderBottom: i < COMPARISONS.length - 1 ? "1px solid var(--line)" : "none" }}>
                <div className="font-display text-lg md:text-xl" style={{ letterSpacing: "-0.01em" }}>{c.dim}</div>
                <div style={{ color: "var(--text-3)", lineHeight: 1.6 }}>{c.trad}</div>
                <div style={{ color: "var(--text)", lineHeight: 1.6 }}>{c.di}</div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-8">
            <div className="p-8 glass-card">
              <Leaf size={22} style={{ color: "var(--gold)" }} strokeWidth={1.4} />
              <h4 className="mt-6 font-display text-xl" style={{ letterSpacing: "-0.02em" }}>Zero surfactant runoff</h4>
              <p className="mt-4 font-body" style={{ color: "var(--text-2)", lineHeight: 1.65, fontSize: 15 }}>Pure water only. Never a drop of soap in your storm drain — a hard requirement in many municipalities.</p>
            </div>
            <div className="p-8 glass-card">
              <Zap size={22} style={{ color: "var(--gold)" }} strokeWidth={1.4} />
              <h4 className="mt-6 font-display text-xl" style={{ letterSpacing: "-0.02em" }}>50-foot ground-based reach</h4>
              <p className="mt-4 font-body" style={{ color: "var(--text-2)", lineHeight: 1.65, fontSize: 15 }}>Water-fed carbon-fibre pole clears up to 5 stories with zero fall risk. Insurance carriers love it.</p>
            </div>
            <div className="p-8 glass-card">
              <Sparkles size={22} style={{ color: "var(--gold)" }} strokeWidth={1.4} />
              <h4 className="mt-6 font-display text-xl" style={{ letterSpacing: "-0.02em" }}>40–50% longer between cleans</h4>
              <p className="mt-4 font-body" style={{ color: "var(--text-2)", lineHeight: 1.65, fontSize: 15 }}>Residue-free glass doesn't magnetise dust. Cadence stretches. Annual bill drops.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-10">
            <div className="md:col-span-3 overline pt-2">Frequently Asked</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              The chemistry, <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>plainly.</span>
            </h2>
          </div>
          <div>
            {FAQ.map((f, i) => <FaqRow key={f.q} q={f.q} a={f.a} i={i} />)}
            <div style={{ borderTop: "1px solid var(--line)" }} />
          </div>
        </div>
      </section>

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg-2)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          See the <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>difference</span> on your building.
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/quote?service=window-cleaning" className="btn-glow" data-testid="di-cta-quote" data-cursor="hover">
            Price Window Service <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
